/**
 * Capacitor bridge. Every function here is a no-op or a browser fallback when
 * running as a plain web app, so the rest of the code never checks the platform.
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Geolocation } from '@capacitor/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { api } from './api';

export const isNative = (): boolean => Capacitor.isNativePlatform();
export const platform = (): 'ios' | 'android' | 'web' => Capacitor.getPlatform() as 'ios' | 'android' | 'web';

/** Where OAuth should send the browser back to. Native uses the app's URL scheme (see ios/android configs). */
export const OAUTH_SCHEME_RETURN = 'bandwagn://auth';

/** One-time native setup: status bar, splash, and the deep-link listener for OAuth returns. */
export async function initNative(onOAuthReturn: (fragment: URLSearchParams) => void): Promise<void> {
  if (!isNative()) return;
  try { await StatusBar.setStyle({ style: Style.Dark }); } catch { /* ignore */ }
  App.addListener('appUrlOpen', ({ url }) => {
    // bandwagn://auth/login#token=…  or  bandwagn://auth/settings#linked=google
    if (!url.startsWith('bandwagn://')) return;
    Browser.close().catch(() => {});
    const hash = url.includes('#') ? url.slice(url.indexOf('#') + 1) : '';
    onOAuthReturn(new URLSearchParams(hash));
  });
  try { await SplashScreen.hide(); } catch { /* ignore */ }
}

/** Open an OAuth start URL in the system browser (native) or navigate (web). */
export async function openAuthUrl(url: string): Promise<void> {
  if (isNative()) await Browser.open({ url, presentationStyle: 'popover' });
  else window.location.href = url;
}

/** Current position via the native plugin when available, else the browser API. */
export async function currentPosition(): Promise<{ lat: number; lng: number }> {
  if (isNative()) {
    const perm = await Geolocation.requestPermissions();
    if (perm.location === 'denied') throw new Error('Location permission denied');
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 10000 });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude };
  }
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('Geolocation not available')); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) => reject(new Error(e.message)),
      { timeout: 10000 },
    );
  });
}

let pushRegistered = false;

/**
 * Ask for push permission and hand the device token to the server. Safe to call
 * repeatedly; does nothing on the web. The server stays dark until APNs/FCM
 * credentials exist, so this can ship ahead of them.
 */
export async function registerPush(): Promise<'registered' | 'denied' | 'unavailable'> {
  if (!isNative()) return 'unavailable';
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return 'denied';
  if (!pushRegistered) {
    pushRegistered = true;
    PushNotifications.addListener('registration', ({ value }) => {
      api.registerPushToken(value, platform() === 'ios' ? 'ios' : 'android').catch(() => {});
    });
    PushNotifications.addListener('registrationError', (e) => console.warn('push registration failed', e));
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      // Deep-link from the payload the server attaches: { kind: 'dm', roomId } | { kind: 'interest', userId }.
      const d = (notification.data ?? {}) as Record<string, string>;
      if (d.kind === 'dm' && d.roomId) window.location.hash = '';
      if (d.kind === 'dm' && d.roomId) window.history.pushState({}, '', `/messages/${d.roomId}`);
      else if (d.kind === 'interest' && d.userId) window.history.pushState({}, '', `/users/${d.userId}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  }
  await PushNotifications.register();
  return 'registered';
}
